import { CountWithComparisonAndChange } from '@/models';
import { Period } from '@/types/date';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { getCountChange } from '@/utils/timeComparison';
import { TimeDimension, TimeDimensionComparison } from '@cubejs-client/core';

export const getValueFromTimeComparisonResult = (
  result: CubeRawRow,
  range: TimeDimensionComparison,
  key: string,
  ignoreCurrentRangePrefix?: boolean,
  changeNotInPercentageValue?: boolean,
): CountWithComparisonAndChange => {
  const current = range.compareDateRange[0][0] as string;
  const previous = range.compareDateRange[1][0] as string;

  const currentEntry = Object.entries(result).find(([k]) => {
    const [time, measure] = k.split(',');

    if (!measure) return k.includes(current) && k.includes(key);
    return time.includes(current) && measure === key;
  });

  const previousEntry = Object.entries(result).find(([k]) => {
    const [time, measure] = k.split(',');
    if (!measure) return k.includes(previous) && k.includes(key);
    return time.includes(previous) && measure === key;
  });

  const currentValue = ignoreCurrentRangePrefix ? result[key] : currentEntry?.[1];
  const previousValue = previousEntry?.[1];

  return {
    current: isNaN(Number(currentValue)) ? 0 : Number(currentValue),
    previous: isNaN(Number(previousValue)) ? 0 : Number(previousValue),
    change: getCountChange(
      { current: Number(currentValue), previous: Number(previousValue) },
      changeNotInPercentageValue,
    ),
  };
};

export const getPercentageChangeFromTimeComparison = (
  result: CubeRawRow,
  range: TimeDimensionComparison,
  key: string,
): CountWithComparisonAndChange => {
  const comparison = getValueFromTimeComparisonResult(result, range, key);

  return {
    ...comparison,
    change: typeof comparison.change === 'number' ? comparison.change * 100 : null,
  };
};

export const getDateRangeTimeDimension = ({
  dimension,
  dateRange,
}: {
  dimension: string;
  dateRange: Period;
}): TimeDimension => {
  const dateFormat = 'YYYY-MM-DD';

  const { beginn: beginnCurrent, end: endCurrent } = dateRange as Period;
  return {
    dimension,
    dateRange: [beginnCurrent.format(dateFormat), endCurrent.format(dateFormat)],
  };
};

// Flattens a time dimension's dateRange (string | [string, string] | undefined) into the
// stable primitives a React Query key needs so a window change forces a refetch.
export const getDateRangeCacheKey = (timeDimension: TimeDimension): string[] =>
  ('dateRange' in timeDimension ? [timeDimension.dateRange] : [])
    .flat()
    .filter((boundary): boundary is string => !!boundary);
